// src/online/code-client.ts
// Browser-side client for the /api/match code-based lobby.

export type MatchStatus = 'lobby' | 'playing';

export interface CreateResp {
  code: string;
  version: number;
  seatsTaken: number;
  maxSeats: number;
  persistent?: boolean;
  /** Host-only secret; required on subsequent start/kick calls. */
  hostToken: string;
  /** True when the server overwrote an existing record because we proved we
   *  were the original host via reclaimHostToken or hostPassword. */
  reclaimed?: boolean;
  /** How reclaim succeeded: 'token' (seamless) or 'password' (fallback). */
  reclaimMethod?: 'token' | 'password';
}
export interface MatchResp {
  token: string;
  version: number;
  updatedAt: number;
  status: MatchStatus;
  seatsTaken: number;
  maxSeats: number;
  joinerNames?: string[];
  slotOccupied?: boolean[];
  persistent?: boolean;
}
export interface JoinResp {
  slot: number;
  token: string;
  version: number;
  seatsTaken: number;
  maxSeats: number;
  joinerNames?: string[];
  slotOccupied?: boolean[];
}
export interface StartResp { version: number; }
export interface UpdateResp { version: number; }
export interface KickResp { version: number; }
export interface ApiError { error: string; message?: string; version?: number; }

export interface CreateOptions {
  /** If provided, use this exact code (4-12 chars, [A-Z0-9]). Code becomes persistent (30d TTL). */
  customCode?: string;
  /** Pass a stored hostToken here to reclaim a persistent code whose server
   *  record still exists (e.g. after a page refresh killed the lobby). Server
   *  matches against the stored record and overwrites if the token matches. */
  reclaimHostToken?: string;
  /** Optional host password (low-value fallback auth). Set on fresh create to
   *  enable reclaim-via-password on a later device. Send on reclaim attempt
   *  when localStorage hostToken is gone. */
  hostPassword?: string;
}

const BASE = '/api/match';

async function asJson<T>(r: Response): Promise<T | ApiError> {
  try {
    return (await r.json()) as T | ApiError;
  } catch {
    return { error: 'bad-response', message: `HTTP ${r.status}` };
  }
}

export async function createMatch(
  token: string,
  maxSeats: number,
  opts: CreateOptions = {},
): Promise<CreateResp | ApiError> {
  const r = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'create',
      token,
      maxSeats,
      customCode: opts.customCode,
      reclaimHostToken: opts.reclaimHostToken,
      hostPassword: opts.hostPassword,
    }),
  });
  return asJson<CreateResp>(r);
}

export async function getMatch(code: string): Promise<MatchResp | ApiError> {
  const r = await fetch(`${BASE}?code=${encodeURIComponent(code)}`);
  return asJson<MatchResp>(r);
}

export async function joinMatch(code: string, name?: string): Promise<JoinResp | ApiError> {
  const r = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'join', code, name }),
  });
  return asJson<JoinResp>(r);
}

export async function startMatch(
  code: string,
  version: number,
  token?: string,
  hostToken?: string,
): Promise<StartResp | ApiError> {
  const r = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'start', code, version, token, hostToken }),
  });
  return asJson<StartResp>(r);
}

/** Host removes a joiner from a lobby. Requires the hostToken returned by createMatch. */
export async function kickMatch(
  code: string,
  slot: number,
  hostToken: string,
): Promise<KickResp | ApiError> {
  const r = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'kick', code, slot, hostToken }),
  });
  return asJson<KickResp>(r);
}

export async function updateMatch(
  code: string,
  token: string,
  version: number,
): Promise<UpdateResp | ApiError> {
  const r = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'update', code, token, version }),
  });
  return asJson<UpdateResp>(r);
}

export function isApiError(x: unknown): x is ApiError {
  return typeof x === 'object' && x !== null && 'error' in x;
}
